import React from "react";
import {
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  InputAdornment,
  TextField,
} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import { createApi } from "../../utils/createApi";
import {
  userFacingApiError,
  withAuthRetry,
} from "../../utils/userFacingApiError";
import { WireGuardServerOption } from "./wireGuardTunnelsInterfaces";
import {
  AccessibleGroup,
  getHighestAccessibleGroup,
  sortGroupsForPicker,
} from "./wireGuardGroupUtils";

interface Props {
  open: boolean;
  onClose: () => void;
  onTunnelCreated: () => Promise<void>;
  wireGuardOptions: WireGuardServerOption[];
  accessibleGroups: AccessibleGroup[];
}

const ExpandableHelperText = ({ text }: { text: string }) => {
  const [expanded, setExpanded] = React.useState(false);

  return (
    <span className="font-mulish text-[12px] text-[#6B7280]">
      <span
        style={{
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: expanded ? "unset" : 1,
          overflow: "hidden",
        }}
      >
        {text}
      </span>
      <button
        type="button"
        className="font-mulish mt-1 p-0 text-[12px] text-[#283E6D] underline"
        onClick={() => setExpanded((prev) => !prev)}
      >
        {expanded ? "Show less" : "Read more"}
      </button>
    </span>
  );
};

const parseSubnetsInput = (subnetsInput: string) => {
  const trimmed = subnetsInput.trim();
  if (!trimmed) {
    return [] as string[];
  }

  if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => (typeof item === "string" ? item.trim() : ""))
          .filter(Boolean);
      }
    } catch {
      // Fall through to comma-separated parsing.
    }
  }

  return trimmed
    .split(",")
    .map((value) =>
      value
        .trim()
        .replace(/^\[/, "")
        .replace(/\]$/, "")
        .replace(/^['"]/, "")
        .replace(/['"]$/, "")
    )
    .filter(Boolean);
};

const SERVER_SELECTION_HELPER_TEXT: Record<string, string> = {
  site_affinity: "Matches the group's existing tunnels",
  round_robin: "Assigned by round robin across zones",
  fallback: "No round-robin pool configured",
};

interface SuggestServerResponse {
  wireguard_server?: string | null;
  selection?: string | null;
}

const initialFormState = {
  wireguard_server: "",
  group_id: "",
  name: "",
  client_public_key: "",
  client_tunnel_ip: "",
  subnets: "",
  subnets_are_virtually_assigned: false,
};

export const WireGuardAddTunnelDialog = ({
  open,
  onClose,
  onTunnelCreated,
  wireGuardOptions,
  accessibleGroups,
}: Props) => {
  const sortedGroups = React.useMemo(
    () => sortGroupsForPicker(accessibleGroups),
    [accessibleGroups]
  );
  const sortedServerOptions = React.useMemo(
    () => [...wireGuardOptions].sort((a, b) => a.name.localeCompare(b.name)),
    [wireGuardOptions]
  );
  const [formState, setFormState] = React.useState(initialFormState);
  // Once the operator picks a server themselves, the round-robin suggestion must
  // stop overwriting it — including when they then change the group.
  const [serverTouched, setServerTouched] = React.useState(false);
  const [serverSelection, setServerSelection] = React.useState<string | null>(
    null
  );
  const [submitting, setSubmitting] = React.useState(false);
  const [isClientTunnelIpLoading, setIsClientTunnelIpLoading] =
    React.useState(false);
  const [cameraCount, setCameraCount] = React.useState("");
  const [cidrPrefixLength, setCidrPrefixLength] = React.useState("24");
  const [isSuggestingSubnet, setIsSuggestingSubnet] = React.useState(false);
  const [subnetSuggestionError, setSubnetSuggestionError] = React.useState<
    string | null
  >(null);
  const [showFailureDialog, setShowFailureDialog] = React.useState(false);
  const [failureErrorMessage, setFailureErrorMessage] = React.useState<
    string | null
  >(null);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    let isMounted = true;
    const AxiosUI = createApi("");
    setIsClientTunnelIpLoading(true);
    setSubnetSuggestionError(null);
    // Group is required (ENG-272) and defaults to the top of the user's tree.
    const defaultGroup = getHighestAccessibleGroup(accessibleGroups);
    setFormState((prev) => ({
      ...prev,
      group_id: prev.group_id || (defaultGroup ? String(defaultGroup.id) : ""),
    }));

    AxiosUI.get<{ client_tunnel_ip?: string }>(
      "wireguard_tunnel/suggest-next-client-ip/"
    )
      .then((response) => {
        if (!isMounted || !response?.data?.client_tunnel_ip) {
          return;
        }
        setFormState((prev) => ({
          ...prev,
          client_tunnel_ip: response.data.client_tunnel_ip || "",
        }));
      })
      .catch(() => {
        // Allow manual entry if suggestion endpoint fails.
      })
      .finally(() => {
        if (isMounted) {
          setIsClientTunnelIpLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [open, accessibleGroups]);

  // ENG-273: the server default comes from the backend, which round-robins across
  // the *-2 zones by how many tunnels the group's top parent account already owns
  // and overrides that with the group's existing zone when it has one. Re-asked
  // whenever the group changes, since the answer depends on it.
  React.useEffect(() => {
    if (!open || serverTouched || !formState.group_id) {
      return;
    }

    let isMounted = true;
    const AxiosUI = createApi("");

    AxiosUI.get<SuggestServerResponse>(
      `wireguard_tunnel/suggest-server/?group_id=${encodeURIComponent(
        formState.group_id
      )}`
    )
      .then((response) => {
        if (!isMounted || !response?.data?.wireguard_server) {
          return;
        }
        setFormState((prev) => ({
          ...prev,
          wireguard_server: response.data.wireguard_server || "",
        }));
        setServerSelection(response.data.selection ?? null);
      })
      .catch(() => {
        // Keep the dialog usable if the suggestion endpoint is unavailable: fall
        // back to the first server so the field is never left blank.
        if (!isMounted) {
          return;
        }
        setFormState((prev) => ({
          ...prev,
          wireguard_server:
            prev.wireguard_server || (sortedServerOptions[0]?.name ?? ""),
        }));
        setServerSelection(null);
      });

    return () => {
      isMounted = false;
    };
  }, [open, serverTouched, formState.group_id, sortedServerOptions]);

  const resetStateAndClose = () => {
    setFormState(initialFormState);
    setSubmitting(false);
    setServerTouched(false);
    setServerSelection(null);
    setCameraCount("");
    setCidrPrefixLength("24");
    setSubnetSuggestionError(null);
    setFailureErrorMessage(null);
    setShowFailureDialog(false);
    onClose();
  };

  const handleSuggestNextSubnet = async () => {
    const AxiosUI = createApi("");
    setSubnetSuggestionError(null);
    setIsSuggestingSubnet(true);

    try {
      const trimmedCameraCount = cameraCount.trim();
      const trimmedPrefixLength = cidrPrefixLength.trim().replace("/", "");
      const queryParam = trimmedCameraCount
        ? `camera_count=${encodeURIComponent(trimmedCameraCount)}`
        : `prefix_length=${encodeURIComponent(trimmedPrefixLength || "24")}`;
      const response = await AxiosUI.get<{ subnet?: string }>(
        `wireguard_tunnel/suggest-next-subnet/?${queryParam}`
      );
      const suggestedSubnet = response?.data?.subnet;
      if (!suggestedSubnet) {
        setSubnetSuggestionError("Unable to suggest a subnet right now.");
        return;
      }
      setFormState((prev) => ({
        ...prev,
        subnets: `["${suggestedSubnet}"]`,
      }));
    } catch {
      setSubnetSuggestionError("Unable to suggest a subnet right now.");
    } finally {
      setIsSuggestingSubnet(false);
    }
  };

  const handleSubmit = async () => {
    const payload = {
      name: formState.name.trim(),
      wireguard_server: formState.wireguard_server,
      group_id: formState.group_id ? Number(formState.group_id) : undefined,
      client_public_key: formState.client_public_key.trim(),
      client_tunnel_ip: formState.client_tunnel_ip.trim() || undefined,
      subnets: parseSubnetsInput(formState.subnets),
      subnets_are_virtually_assigned: formState.subnets_are_virtually_assigned,
    };

    setSubmitting(true);
    try {
      // The client is built inside the request so a session renewed by
      // withAuthRetry is what the retry sends (ENG-2002).
      await withAuthRetry(() =>
        createApi("").post("wireguard_tunnel/", payload)
      );
      await onTunnelCreated();
      resetStateAndClose();
    } catch (reason) {
      setFailureErrorMessage(userFacingApiError(reason));
      setShowFailureDialog(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={resetStateAndClose}
        fullWidth
        maxWidth="sm"
        aria-labelledby="add-wireguard-tunnel-title"
        sx={{ "& *": { fontFamily: "Mulish" } }}
      >
        <DialogTitle
          id="add-wireguard-tunnel-title"
          sx={{ fontFamily: "Mulish", fontWeight: 700, color: "#283E6D" }}
        >
          Add WireGuard Tunnel
        </DialogTitle>
        <DialogContent dividers sx={{ px: 4, py: 3 }}>
          <div className="flex flex-col gap-5">
            <TextField
              id="wireguard_server"
              select
              SelectProps={{ native: true }}
              value={formState.wireguard_server}
              label="WireGuard Server"
              required
              helperText={
                serverSelection
                  ? SERVER_SELECTION_HELPER_TEXT[serverSelection] ?? undefined
                  : undefined
              }
              onChange={(event) => {
                // Captured before the updater runs: React restores the controlled
                // input's DOM value first, so a lazy read returns the old value.
                const selectedServer = event.target.value;
                setServerTouched(true);
                setServerSelection(null);
                setFormState((prev) => ({
                  ...prev,
                  wireguard_server: selectedServer,
                }));
              }}
              variant="standard"
              sx={{ width: "100%", "& *": { fontFamily: "Mulish" } }}
            >
              <option aria-label="None" value="" />
              {sortedServerOptions.map((option) => (
                <option key={option.id} value={option.name}>
                  {option.name}
                </option>
              ))}
            </TextField>

            <Autocomplete
              id="group_id"
              options={sortedGroups}
              value={
                sortedGroups.find(
                  (group) => String(group.id) === formState.group_id
                ) || null
              }
              onChange={(_, value) => {
                if (!value) {
                  // disableClearable keeps this from firing once a group is set;
                  // ignoring an empty change means the field can never go blank.
                  return;
                }
                setFormState((prev) => ({
                  ...prev,
                  group_id: String(value.id),
                }));
              }}
              getOptionLabel={(option) => option.name || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disableClearable={Boolean(formState.group_id)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Group"
                  required
                  helperText="*Required — the tunnel is only visible to users of this group"
                  variant="standard"
                  sx={{ width: "100%", "& *": { fontFamily: "Mulish" } }}
                />
              )}
            />

            <TextField
              id="wireguard_name"
              value={formState.name}
              variant="standard"
              label="Name"
              required
              onChange={(event) => {
                // Captured eagerly: read inside the updater it comes back
                // stale under fireEvent/act(), so the field can't be
                // exercised in tests. Identical behaviour in the browser.
                const nextValue = event.target.value;
                setFormState((prev) => ({ ...prev, name: nextValue }));
              }}
              sx={{ width: "100%", "& *": { fontFamily: "Mulish" } }}
              type="string"
            />

            <TextField
              id="client_public_key"
              value={formState.client_public_key}
              variant="standard"
              label="Client Public Key"
              required
              helperText="WireGuard client public key (must be unique)"
              onChange={(event) => {
                // Captured eagerly: read inside the updater it comes back
                // stale under fireEvent/act(), so the field can't be
                // exercised in tests. Identical behaviour in the browser.
                const nextValue = event.target.value;
                setFormState((prev) => ({
                  ...prev,
                  client_public_key: nextValue,
                }));
              }}
              sx={{ width: "100%", "& *": { fontFamily: "Mulish" } }}
              type="string"
            />

            <TextField
              id="client_tunnel_ip"
              value={formState.client_tunnel_ip}
              variant="standard"
              label="Client Tunnel IP"
              helperText={
                <ExpandableHelperText text="Client tunnel IP address (must end with /32, e.g., '10.0.0.1/32'). By default this is pre-populated with the first available IP from the pool, but you can change it if the client already has one assigned." />
              }
              disabled={isClientTunnelIpLoading}
              InputProps={{
                endAdornment: isClientTunnelIpLoading ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : undefined,
              }}
              onChange={(event) =>
                setFormState((prev) => ({
                  ...prev,
                  client_tunnel_ip: event.target.value,
                }))
              }
              sx={{ width: "100%", "& *": { fontFamily: "Mulish" } }}
              type="string"
            />

            <TextField
              value={formState.subnets}
              label="Subnets"
              multiline
              minRows={2}
              onChange={(event) =>
                setFormState((prev) => ({
                  ...prev,
                  subnets: event.target.value,
                }))
              }
              sx={{ width: "100%", "& *": { fontFamily: "Mulish" } }}
              helperText={
                <ExpandableHelperText
                  text={`The range of IP addresses on the customer's local network, in CIDR format (e.g., ["192.168.1.0/24"]). If the NVR/recording server is on the same machine as the WireGuard client, you can leave this empty. If the cameras are on a different IP range (e.g., the client routes traffic across their network), enter the local subnet(s) where their cameras live. Always use the smallest subnet that covers the required hosts - a /32 for a single IP is better than a /24 for an entire block.`}
                />
              }
            />

            <div className="flex gap-4 pl-12">
              <TextField
                value={cameraCount}
                label="Approx camera count"
                type="number"
                onChange={(event) => {
                  setSubnetSuggestionError(null);
                  setCameraCount(event.target.value);
                }}
                placeholder="-"
                sx={{ width: "220px", "& *": { fontFamily: "Mulish" } }}
                helperText="Leave blank if unknown"
              />
              <TextField
                value={cidrPrefixLength}
                label="CIDR size"
                type="number"
                onChange={(event) => {
                  setSubnetSuggestionError(null);
                  setCidrPrefixLength(event.target.value);
                }}
                sx={{ width: "120px", "& *": { fontFamily: "Mulish" } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">/</InputAdornment>
                  ),
                }}
              />
            </div>

            <div className="pl-12">
              <OrangeTextButton
                onClick={handleSuggestNextSubnet}
                text={
                  isSuggestingSubnet ? "Suggesting..." : "Suggest Next Subnet"
                }
                disabled={isSuggestingSubnet}
              />
            </div>

            {subnetSuggestionError && (
              <FormHelperText
                className="font-mulish mt-1 text-red-600"
                role="alert"
              >
                {subnetSuggestionError}
              </FormHelperText>
            )}

            <div>
              <FormControlLabel
                sx={{ fontFamily: "Mulish", color: "#001943" }}
                label="Virtually Assign Subnets"
                control={
                  <Checkbox
                    color="primary"
                    checked={formState.subnets_are_virtually_assigned}
                    onChange={(_, checked) =>
                      setFormState((prev) => ({
                        ...prev,
                        subnets_are_virtually_assigned: checked,
                      }))
                    }
                  />
                }
              />
              <FormHelperText>
                <ExpandableHelperText
                  text={`Check this if the customer's real subnet overlaps with an existing tunnel's subnet. When enabled, the customer sets up DNAT/SNAT on their side to translate traffic to a virtual IP range, and you enter that virtual range in the Subnets field above. This avoids routing conflicts while keeping both tunnels operational.`}
                />
              </FormHelperText>
            </div>
          </div>
        </DialogContent>
        <DialogActions sx={{ p: 2, px: 4 }}>
          <OrangeTextButton
            onClick={resetStateAndClose}
            text="Cancel"
            disabled={submitting}
          />
          <OrangeTextButton
            onClick={handleSubmit}
            text={submitting ? "Submitting..." : "Submit"}
            disabled={
              submitting ||
              !formState.wireguard_server ||
              !formState.group_id ||
              !formState.name.trim() ||
              !formState.client_public_key.trim()
            }
          />
        </DialogActions>
      </Dialog>

      <Dialog
        open={showFailureDialog}
        onClose={() => {
          setShowFailureDialog(false);
          setFailureErrorMessage(null);
        }}
        aria-labelledby="wireguard-failure-dialog-title"
        sx={{ "& *": { fontFamily: "Mulish" } }}
      >
        <DialogTitle
          id="wireguard-failure-dialog-title"
          sx={{ fontFamily: "Mulish" }}
        >
          WireGuard configuration could not be saved
        </DialogTitle>
        <DialogContent sx={{ fontFamily: "Mulish" }}>
          {failureErrorMessage && (
            <p
              className="font-mulish mb-3 text-[14px] text-red-600"
              role="alert"
            >
              {failureErrorMessage}
            </p>
          )}
          <p>Please review the values and try again.</p>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <OrangeTextButton
            onClick={() => {
              setShowFailureDialog(false);
              setFailureErrorMessage(null);
            }}
            text="Try Again"
            disabled={false}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};
